import { useEffect, useRef, useState } from 'react';
import Image from '@/lib/media/image';
import Link from '@/lib/nav/link';
import { createPortal } from 'react-dom';
import { RoamIcon } from '@/components/icons/RoamIcon';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { subscribeOpenPhoneNav } from '@/lib/nav/open-phone-nav';
import { SidebarNav } from '@/components/SidebarNav';
import type { TripOption } from '@/components/TripSwitcher';

/**
 * Shared floating hamburger bubble — 44px target, frosted white disk so the
 * control stays readable over scrolling page content (settings, lists, etc.).
 */
export const APP_NAV_MENU_BUTTON_CLASS =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white/95 text-ink shadow-[0_2px_10px_rgba(31,28,28,0.12),0_0_0_1px_rgba(31,28,28,0.04)] backdrop-blur-md transition hover:bg-white hover:shadow-[0_4px_14px_rgba(31,28,28,0.16)] supports-[backdrop-filter]:bg-white/88';

export type AppNavigationMenuProps = {
  email: string;
  trips: TripOption[];
  lastActiveTripId?: string | null;
  triggerClassName?: string;
  showTripSwitcher?: boolean;
  showAdminNav?: boolean;
  /** Show at md+ too. With onDockedOpenChange, use a docked rail; otherwise use a sheet. */
  showOnDesktop?: boolean;
  dockedOpen?: boolean;
  onDockedOpenChange?: (open: boolean) => void;
};

/**
 * One hamburger control and drawer for the whole signed-in app. Same items,
 * logo header, and sheet styling on Explore, trips, settings, help, and
 * anywhere else that mounts this component.
 */
export function AppNavigationMenu({
  email,
  trips,
  lastActiveTripId = null,
  triggerClassName,
  showTripSwitcher = true,
  showAdminNav = false,
  showOnDesktop = false,
  dockedOpen = false,
  onDockedOpenChange,
}: AppNavigationMenuProps) {
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const usesDockedDrawer = showOnDesktop && Boolean(onDockedOpenChange);
  const drawerTitle = 'Navigation';
  const drawerDescription = 'Switch between trips, Explore, and account settings.';

  useEffect(() => subscribeOpenPhoneNav(() => setOpen(true)), []);

  useEffect(() => {
    setMounted(true);
    const query = window.matchMedia('(min-width: 768px)');
    const updateDesktopState = () => setIsDesktop(query.matches);
    updateDesktopState();
    query.addEventListener('change', updateDesktopState);
    return () => query.removeEventListener('change', updateDesktopState);
  }, []);

  function openMenu() {
    if (showOnDesktop && onDockedOpenChange && window.matchMedia('(min-width: 768px)').matches) {
      onDockedOpenChange(!dockedOpen);
      return;
    }
    setOpen(true);
  }

  const dockedDrawer =
    usesDockedDrawer && mounted
      ? createPortal(
          <aside
            className={cn(
              'fixed inset-y-0 left-0 z-40 hidden w-72 flex-col bg-white transition-transform duration-300 ease-out safe-pt safe-pb md:flex',
              dockedOpen
                ? 'translate-x-0 border-r border-ink/10 shadow-xl shadow-ink/10'
                : 'pointer-events-none -translate-x-full border-transparent shadow-none',
            )}
            aria-hidden={!dockedOpen}
            inert={!dockedOpen ? true : undefined}
          >
            <div className="absolute right-5 top-5 z-10">
              <button
                type="button"
                onClick={() => onDockedOpenChange?.(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink/55 transition hover:bg-muted hover:text-ink"
                aria-label="Collapse navigation menu"
              >
                <RoamIcon name="close" className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <AppNavigationDrawerContents
              email={email}
              trips={trips}
              lastActiveTripId={lastActiveTripId}
              showAdminNav={showAdminNav}
              showTripSwitcher={showTripSwitcher}
              onNavigate={undefined}
            />
          </aside>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        aria-haspopup="dialog"
        aria-expanded={isDesktop && usesDockedDrawer ? dockedOpen : open}
        className={cn(
          APP_NAV_MENU_BUTTON_CLASS,
          showOnDesktop ? 'inline-flex' : 'inline-flex md:hidden',
          triggerClassName,
        )}
        aria-label="Open navigation menu"
      >
        <RoamIcon name="menu" className="h-5 w-5" aria-hidden />
      </button>

      {dockedDrawer}

      {isDesktop && (!showOnDesktop || usesDockedDrawer) ? null : (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="left"
            className="flex w-72 max-w-[88vw] flex-col overflow-y-auto p-0 safe-pt safe-pb"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          >
            <SheetTitle className="sr-only">{drawerTitle}</SheetTitle>
            <SheetDescription className="sr-only">{drawerDescription}</SheetDescription>
            <AppNavigationDrawerContents
              email={email}
              trips={trips}
              lastActiveTripId={lastActiveTripId}
              showAdminNav={showAdminNav}
              showTripSwitcher={showTripSwitcher}
              onNavigate={() => setOpen(false)}
            />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

export function AppNavigationDrawerContents({
  email,
  trips,
  lastActiveTripId = null,
  showAdminNav,
  showTripSwitcher,
  onNavigate,
}: {
  email: string;
  trips: TripOption[];
  lastActiveTripId?: string | null;
  showAdminNav: boolean;
  showTripSwitcher: boolean;
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="flex justify-center px-5 pt-6 pb-5">
        <Link
          href="/trips/active"
          className="inline-flex items-center"
          aria-label="Roam Together"
          onClick={onNavigate}
        >
          <Image
            src="/assets/logos/Main_Logo.png"
            alt="Roam Together"
            width={160}
            height={72}
            className="h-12 w-auto object-contain"
          />
        </Link>
      </div>
      <SidebarNav
        email={email}
        trips={trips}
        lastActiveTripId={lastActiveTripId}
        showAdminNav={showAdminNav}
        showTripSwitcher={showTripSwitcher}
        onNavigate={onNavigate}
      />
    </>
  );
}
